import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Copy,
  KeyRound,
  Loader2,
  Lock,
  Paperclip,
  Pencil,
  Play,
  ShieldCheck,
  X,
} from "lucide-react";
import { Card, CardBody } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import { Field, Input, Select, Textarea } from "../components/ui/Input";
import { Spinner } from "../components/ui/Spinner";
import { useAuth } from "../lib/auth";
import { getEndpoint } from "../lib/api";
import type { ApiEndpointDetail } from "../lib/api";
import { exampleFromSchema } from "../lib/schemaExample";
import { cn } from "../lib/cn";

const methodTone: Record<string, string> = {
  GET: "bg-emerald-100 text-emerald-700",
  POST: "bg-brand-100 text-brand-700",
  PUT: "bg-amber-100 text-amber-700",
  PATCH: "bg-amber-100 text-amber-700",
  DELETE: "bg-rose-100 text-rose-700",
};

interface FileVal {
  b64: string;
  name: string;
  size: number;
  type: string;
}
interface RunResult {
  status: number;
  statusText: string;
  timeMs: number;
  headers: [string, string][];
  body: string;
  error?: string;
}
type Spec = Record<string, unknown>;
type Schema = Record<string, unknown> | null | undefined;

/** Whether a schema property carries uploaded document(s) and, if so, whether it
 *  takes one file ("single") or many ("multi", an array of byte/binary strings). */
function fileFieldKind(name: string, spec: Spec): "single" | "multi" | null {
  const fmt = spec?.format;
  const isByteString = (s: Spec | undefined) =>
    s?.type === "string" && (s?.format === "byte" || s?.format === "binary");
  if (spec?.type === "array" && isByteString(spec?.items as Spec | undefined)) return "multi";
  if (spec?.type === "string" && (fmt === "byte" || fmt === "binary")) return "single";
  if (["content", "file", "document"].includes(name)) return "single";
  return null;
}
function isFileField(name: string, spec: Spec): boolean {
  return fileFieldKind(name, spec) !== null;
}
function schemaProps(schema: Schema): Record<string, Spec> {
  return (schema as { properties?: Record<string, Spec> } | null)?.properties ?? {};
}

function pretty(text: string): string {
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
}

function CopyBtn({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        void navigator.clipboard?.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
      className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
      aria-label="Copy"
    >
      {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
    </button>
  );
}

export default function ApiConsole() {
  const { slug } = useParams<{ slug: string }>();
  const { currentTenant } = useAuth();
  const tenant = currentTenant?.slug ?? "";
  const navigate = useNavigate();

  const [ep, setEp] = useState<ApiEndpointDetail | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  const [query, setQuery] = useState("");
  const [body, setBody] = useState("");
  // auth
  const [basicUser, setBasicUser] = useState("");
  const [basicPass, setBasicPass] = useState("");
  const [ccId, setCcId] = useState("");
  const [ccSecret, setCcSecret] = useState("");
  const [token, setToken] = useState("");
  const [tokenBusy, setTokenBusy] = useState(false);
  const [tokenMsg, setTokenMsg] = useState<string | null>(null);
  const [jwtToken, setJwtToken] = useState("");
  // uploaded files per schema field (payslips, bank_statements, content…). A
  // "single" field holds a list of length ≤1; a "multi" field holds several.
  const [files, setFiles] = useState<Record<string, FileVal[]>>({});
  // response
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<RunResult | null>(null);

  useEffect(() => {
    if (!tenant || !slug) return;
    getEndpoint(tenant, slug)
      .then((e) => {
        setEp(e);
        setFiles({});
        if (e.method !== "GET") {
          const props = schemaProps(e.request_schema);
          const example = (exampleFromSchema(e.request_schema) ?? {}) as Record<string, unknown>;
          // File fields are handled by the picker, not the JSON body.
          for (const [k, spec] of Object.entries(props)) if (isFileField(k, spec)) delete example[k];
          setBody(Object.keys(example).length ? JSON.stringify(example, null, 2) : "{}");
        }
      })
      .catch(() => setLoadErr("Couldn't load this endpoint."));
  }, [tenant, slug]);

  const gatewayPath = useMemo(() => (ep ? `/api/gw/${tenant}/${ep.path}` : ""), [ep, tenant]);
  const props = schemaProps(ep?.request_schema);
  const fileFields = Object.keys(props).filter((k) => isFileField(k, props[k]));
  const scalarFields = Object.keys(props).filter((k) => !isFileField(k, props[k]));
  const isMultiField = (k: string) => fileFieldKind(k, props[k] ?? {}) === "multi";
  // A schema-less body (or "content"-only) still gets one generic file picker.
  const genericFile = ep && ep.method !== "GET" && fileFields.length === 0 && Object.keys(props).length === 0;

  function bodyObj(): Record<string, unknown> {
    try {
      return body.trim() ? (JSON.parse(body) as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }
  function setField(name: string, value: unknown) {
    const obj = bodyObj();
    obj[name] = value;
    setBody(JSON.stringify(obj, null, 2));
  }

  function readAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result).split(",")[1] || "");
      r.onerror = () => reject(new Error("could not read the file"));
      r.readAsDataURL(file);
    });
  }
  async function addFile(field: string, file: File, multi: boolean) {
    const b64 = await readAsBase64(file);
    const val: FileVal = { b64, name: file.name, size: file.size, type: file.type };
    setFiles((prev) => ({ ...prev, [field]: multi ? [...(prev[field] ?? []), val] : [val] }));
  }
  function removeFile(field: string, index: number) {
    setFiles((prev) => {
      const list = (prev[field] ?? []).filter((_, i) => i !== index);
      const next = { ...prev };
      if (list.length) next[field] = list;
      else delete next[field];
      return next;
    });
  }

  /** JSON body with uploaded files merged in: a multi field becomes an array of
   *  base64 (+ a parallel mime-type array); a single field one base64 value. */
  function effectiveBody(): string {
    const entries = Object.entries(files).filter(([, list]) => list.length);
    if (!entries.length) return body;
    let obj: Record<string, unknown>;
    try {
      obj = body.trim() ? JSON.parse(body) : {};
    } catch {
      obj = {};
    }
    for (const [field, list] of entries) {
      if (isMultiField(field)) {
        obj[field] = list.map((f) => f.b64);
        obj[`${field}_mime_type`] = list.map((f) => f.type || "application/pdf");
      } else {
        obj[field] = list[0].b64;
        if (!obj[`${field}_mime_type`]) obj[`${field}_mime_type`] = list[0].type || "application/pdf";
      }
    }
    return JSON.stringify(obj);
  }

  function authHeader(): string {
    if (!ep) return "";
    if (ep.auth_type === "basic") return "Basic " + btoa(`${basicUser}:${basicPass}`);
    if (ep.auth_type === "client_credentials") return token ? `Bearer ${token}` : "";
    if (ep.auth_type === "jwt") return jwtToken ? `Bearer ${jwtToken}` : "";
    return "";
  }

  async function getToken() {
    setTokenBusy(true);
    setTokenMsg(null);
    try {
      const res = await fetch(`/api/gw/${tenant}/token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client_id: ccId, client_secret: ccSecret }),
      });
      const data = await res.json();
      if (!res.ok) {
        setTokenMsg(data.error || "Token request failed.");
        return;
      }
      setToken(data.access_token);
      setTokenMsg(`Token acquired (expires in ${data.expires_in}s).`);
    } catch {
      setTokenMsg("Couldn't reach the token endpoint.");
    } finally {
      setTokenBusy(false);
    }
  }

  async function send() {
    if (!ep) return;
    setSending(true);
    setResult(null);
    const headers: Record<string, string> = {};
    const auth = authHeader();
    if (auth) headers["Authorization"] = auth;
    const hasBody = ep.method !== "GET" && ep.method !== "DELETE";
    if (hasBody) headers["Content-Type"] = "application/json";

    const started = performance.now();
    try {
      const res = await fetch(`${gatewayPath}${query}`, {
        method: ep.method,
        headers,
        body: hasBody ? effectiveBody() : undefined,
      });
      const text = await res.text();
      setResult({
        status: res.status, statusText: res.statusText,
        timeMs: Math.round(performance.now() - started),
        headers: [...res.headers.entries()], body: text,
      });
    } catch (e) {
      setResult({
        status: 0, statusText: "", timeMs: Math.round(performance.now() - started),
        headers: [], body: "", error: (e as Error).message || "Request failed",
      });
    } finally {
      setSending(false);
    }
  }

  const curl = useMemo(() => {
    if (!ep) return "";
    const origin = window.location.origin;
    const lines = [`curl -X ${ep.method} ${origin}${gatewayPath}${query} \\`];
    const auth = authHeader();
    if (auth) lines.push(`  -H 'Authorization: ${auth.startsWith("Bearer") ? "Bearer <token>" : auth}' \\`);
    if (ep.method !== "GET" && ep.method !== "DELETE") {
      lines.push(`  -H 'Content-Type: application/json' \\`);
      lines.push(`  -d '${effectiveBody().replace(/\n\s*/g, "")}'`);
    } else {
      lines[lines.length - 1] = lines[lines.length - 1].replace(/ \\$/, "");
    }
    return lines.join("\n");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ep, gatewayPath, query, body, files, token, jwtToken, basicUser, basicPass]);

  if (loadErr) {
    return (
      <Card className="px-6 py-16 text-center">
        <p className="font-semibold text-slate-800">{loadErr}</p>
        <Link to="/app/apis" className="mt-3 inline-block text-sm text-brand-700">← Back to APIs</Link>
      </Card>
    );
  }
  if (!ep) return <div className="flex justify-center py-24"><Spinner className="h-8 w-8 text-brand-500" /></div>;

  const statusTone = (s: number) =>
    s >= 200 && s < 300 ? "text-emerald-600" : s >= 400 ? "text-rose-600" : s === 0 ? "text-slate-500" : "text-amber-600";

  return (
    <>
      <div className="mb-2 flex items-center justify-between">
        <Link to="/app/apis" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
          <ArrowLeft className="h-4 w-4" /> APIs
        </Link>
        <Button variant="outline" size="sm" onClick={() => navigate("/app/apis", { state: { edit: ep.slug } })}>
          <Pencil className="h-3.5 w-3.5" /> Edit design
        </Button>
      </div>

      <Card className="mb-5">
        <CardBody>
          <div className="flex flex-wrap items-center gap-3">
            <span className={cn("rounded-lg px-2.5 py-1 text-xs font-bold", methodTone[ep.method] ?? "bg-slate-100 text-slate-600")}>{ep.method}</span>
            <code className="flex-1 break-all text-sm text-slate-700">{gatewayPath}</code>
            <CopyBtn value={`${window.location.origin}${gatewayPath}`} />
            <span className="inline-flex items-center gap-1 text-xs text-slate-500">
              {ep.auth_type === "none" ? <Lock className="h-3.5 w-3.5 text-amber-500" /> : <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />}
              {ep.auth_type === "none" ? "No auth" : ep.auth_type.replace("_", " ")}
            </span>
            <Badge tone={ep.mode === "mock" ? "violet" : "brand"}>{ep.mode}</Badge>
          </div>
          <h1 className="mt-3 text-xl font-bold text-slate-900">{ep.name}</h1>
          {ep.description && <p className="mt-1 text-sm text-slate-500">{ep.description}</p>}
        </CardBody>
      </Card>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Request */}
        <Card>
          <CardBody className="space-y-4">
            <h2 className="text-sm font-semibold text-slate-900">Request</h2>

            {ep.auth_type !== "none" && (
              <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-3">
                <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  <KeyRound className="h-3.5 w-3.5" /> Authorization
                </p>
                {ep.auth_type === "basic" && (
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Input placeholder="username" value={basicUser} onChange={(e) => setBasicUser(e.target.value)} />
                    <Input type="password" placeholder="password" value={basicPass} onChange={(e) => setBasicPass(e.target.value)} />
                  </div>
                )}
                {ep.auth_type === "client_credentials" && (
                  <div className="space-y-2">
                    <div className="grid gap-2 sm:grid-cols-2">
                      <Input placeholder="client_id" value={ccId} onChange={(e) => setCcId(e.target.value)} />
                      <Input type="password" placeholder="client_secret" value={ccSecret} onChange={(e) => setCcSecret(e.target.value)} />
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={getToken} loading={tokenBusy} disabled={!ccId || !ccSecret}>Get token</Button>
                      {token && <span className="text-xs text-emerald-600">✓ token ready</span>}
                    </div>
                    {tokenMsg && <p className={cn("text-xs", token ? "text-emerald-600" : "text-rose-600")}>{tokenMsg}</p>}
                  </div>
                )}
                {ep.auth_type === "jwt" && (
                  <Textarea rows={2} className="font-mono text-xs" placeholder="paste your signed JWT (HS256, shared secret)"
                    value={jwtToken} onChange={(e) => setJwtToken(e.target.value)} />
                )}
              </div>
            )}

            <Field label="Query string" hint="Optional. e.g. ?limit=5&status=paid">
              <Input value={query} onChange={(e) => setQuery(e.target.value)} className="font-mono text-xs" />
            </Field>

            {/* File pickers — one control per document field (multi allows several) */}
            {fileFields.map((k) => (
              <FilePicker key={k} label={props[k]?.description ? String(props[k].description).split(".")[0] : k}
                fieldName={k} multi={isMultiField(k)} list={files[k] ?? []}
                onPick={(f) => addFile(k, f, isMultiField(k))} onRemove={(i) => removeFile(k, i)} />
            ))}
            {genericFile && (
              <FilePicker label="Upload a file" fieldName="content" multi={false} list={files.content ?? []}
                onPick={(f) => addFile("content", f, false)} onRemove={(i) => removeFile("content", i)} />
            )}

            {/* Scalar fields — enums become dropdowns */}
            {scalarFields.length > 0 && (
              <div className="space-y-3">
                {scalarFields.map((k) => (
                  <SchemaField key={k} name={k} spec={props[k]} value={bodyObj()[k]} onChange={setField} />
                ))}
              </div>
            )}

            {/* Raw JSON — collapsible when we have a form, primary when we don't */}
            {ep.method !== "GET" &&
              (scalarFields.length > 0 || fileFields.length > 0 ? (
                <details className="text-xs">
                  <summary className="cursor-pointer text-slate-400 hover:text-slate-600">Edit raw JSON</summary>
                  <Textarea rows={7} className="mt-2 font-mono text-xs" spellCheck={false} value={body} onChange={(e) => setBody(e.target.value)} />
                </details>
              ) : (
                <Field label="Request body" hint="JSON body.">
                  <Textarea rows={genericFile && files.content ? 6 : 10} className="font-mono text-xs" spellCheck={false} value={body} onChange={(e) => setBody(e.target.value)} />
                </Field>
              ))}

            <Button className="w-full" onClick={send} loading={sending}><Play className="h-4 w-4" /> Send request</Button>

            <details className="text-xs">
              <summary className="cursor-pointer text-slate-400 hover:text-slate-600">Copy as curl</summary>
              <pre className="mt-2 overflow-auto rounded-lg bg-slate-900 p-3 text-[11px] leading-relaxed text-slate-200">{curl}</pre>
            </details>
          </CardBody>
        </Card>

        {/* Response */}
        <Card>
          <CardBody className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-900">Response</h2>
              {result && !result.error && (
                <span className="text-xs text-slate-400">
                  <span className={cn("font-bold", statusTone(result.status))}>{result.status} {result.statusText}</span>
                  {" · "}{result.timeMs} ms
                </span>
              )}
            </div>
            {sending ? (
              <div className="flex items-center gap-2 py-10 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Sending…</div>
            ) : !result ? (
              <p className="py-10 text-center text-sm text-slate-400">Send a request to see the response.</p>
            ) : result.error ? (
              <div className="flex items-start gap-2 rounded-xl bg-rose-50 px-3.5 py-3 text-sm text-rose-700"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {result.error}</div>
            ) : (
              <>
                <div className="relative">
                  <div className="absolute right-1 top-1"><CopyBtn value={pretty(result.body)} /></div>
                  <pre className="max-h-80 overflow-auto rounded-xl bg-slate-900 p-3 text-xs leading-relaxed text-slate-100">{pretty(result.body) || "(empty body)"}</pre>
                </div>
                {result.headers.length > 0 && (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-slate-400 hover:text-slate-600">Response headers</summary>
                    <div className="mt-2 space-y-0.5 font-mono text-[11px] text-slate-500">
                      {result.headers.map(([k, v]) => <div key={k}><span className="text-slate-400">{k}:</span> {v}</div>)}
                    </div>
                  </details>
                )}
              </>
            )}
          </CardBody>
        </Card>
      </div>

      {(ep.request_schema || ep.response_schema) && (
        <div className="mt-5 grid gap-5 lg:grid-cols-2">
          {ep.request_schema && <SchemaDoc title="Request schema" schema={ep.request_schema} />}
          {ep.response_schema && <SchemaDoc title="Response schema" schema={ep.response_schema} />}
        </div>
      )}
    </>
  );
}

function FilePicker({ label, fieldName, multi, list, onPick, onRemove }: {
  label: string; fieldName: string; multi: boolean; list: FileVal[];
  onPick: (f: File) => void; onRemove: (index: number) => void;
}) {
  return (
    <div>
      <p className="mb-1.5 text-sm font-semibold text-slate-700">
        {label}
        {multi && <span className="ml-1.5 font-normal text-slate-400">· add several</span>}
      </p>
      <div className="space-y-2">
        {list.map((file, i) => (
          <div key={i} className="flex items-center gap-3 rounded-xl border border-brand-200 bg-brand-50/50 px-3.5 py-2.5">
            <Paperclip className="h-4 w-4 shrink-0 text-brand-600" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-slate-800">{file.name}</p>
              <p className="text-xs text-slate-400">{(file.size / 1024).toFixed(1)} KB · {file.type || "unknown"} · sent as “{fieldName}”</p>
            </div>
            <button onClick={() => onRemove(i)} className="rounded-lg p-1.5 text-slate-400 hover:bg-white hover:text-rose-500" aria-label="Remove file"><X className="h-4 w-4" /></button>
          </div>
        ))}
        {(multi || list.length === 0) && (
          <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 bg-white/60 py-3 text-sm font-medium text-slate-500 transition hover:border-brand-300 hover:bg-brand-50/40 hover:text-brand-700">
            <Paperclip className="h-4 w-4" /> {list.length ? "Add another file" : "Choose a file (PDF or image)"}
            <input type="file" accept="application/pdf,image/*" className="hidden"
              onChange={(e) => { if (e.target.files?.[0]) { onPick(e.target.files[0]); e.target.value = ""; } }} />
          </label>
        )}
      </div>
    </div>
  );
}

function SchemaField({ name, spec, value, onChange }: {
  name: string; spec: Spec; value: unknown; onChange: (name: string, value: unknown) => void;
}) {
  const hint = spec?.description as string | undefined;
  const enumVals = spec?.enum as unknown[] | undefined;
  if (Array.isArray(enumVals)) {
    return (
      <Field label={name} hint={hint}>
        <Select value={String(value ?? "")} onChange={(e) => onChange(name, e.target.value)}>
          {enumVals.map((o) => <option key={String(o)} value={String(o)}>{String(o)}</option>)}
        </Select>
      </Field>
    );
  }
  if (spec?.type === "boolean") {
    return (
      <label className="flex items-center gap-2.5 text-sm text-slate-600">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(name, e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-brand-600" /> {name}
      </label>
    );
  }
  if (spec?.type === "integer" || spec?.type === "number") {
    return (
      <Field label={name} hint={hint}>
        <Input type="number" value={value === undefined || value === null ? "" : String(value)}
          onChange={(e) => onChange(name, spec.type === "integer" ? parseInt(e.target.value || "0", 10) : parseFloat(e.target.value || "0"))} />
      </Field>
    );
  }
  return (
    <Field label={name} hint={hint}>
      <Input value={value === undefined || value === null ? "" : String(value)}
        placeholder={spec?.example ? String(spec.example) : ""} onChange={(e) => onChange(name, e.target.value)} />
    </Field>
  );
}

function SchemaDoc({ title, schema }: { title: string; schema: Record<string, unknown> }) {
  return (
    <Card>
      <CardBody>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">{title}</h3>
        <pre className="max-h-64 overflow-auto rounded-xl bg-slate-50 p-3 text-xs leading-relaxed text-slate-600">{JSON.stringify(schema, null, 2)}</pre>
      </CardBody>
    </Card>
  );
}
